import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Grid,
  Card,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Snackbar,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { API_ENDPOINTS } from "../../../config/api";

const DealerQuotationForm = ({ edit = false, initialData = {}, onSubmit }) => {
  const [searchParams] = useSearchParams();
  const enquiryIdFromUrl = searchParams.get("enquiryId");
  const [customerOrders, setCustomerOrders] = useState([]);
  const [formData, setFormData] = useState({
    id: initialData.id || "",
    productPrices: initialData.productPrices || {},
    bendingCharges: initialData.bendingCharges || "",
    transportationCharges: initialData.transportationCharges || "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [success, setSuccess] = useState(null);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCustomerOrders = async () => {
      setLoading(true);
      try {
        const response = await axios.get(API_ENDPOINTS.CUSTOMER_ORDERS_DETAILS);
        const data = Array.isArray(response.data)
          ? response.data
          : response.data?.data || Object.values(response.data);
        setCustomerOrders(data);
      } catch (error) {
        setError("Failed to load Enquiry. Please try again.");
        setCustomerOrders([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCustomerOrders();
  }, []);

  useEffect(() => {
    const fetchDealers = async () => {
      setLoading(true);
      try {
        const response = await axios.get(API_ENDPOINTS.DEALER_NAMES);
        const data = Array.isArray(response.data.dealers)
          ? response.data.dealers
          : response.data?.data || Object.values(response.data);
        setDealers(data);
      } catch (error) {
        setError("Failed to load Dealers. Please try again.");
        setDealers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDealers();
  }, []);

  useEffect(() => {
    if (!formData.id) {
      setSelectedOrderDetails(null);
      setFormData((prev) => ({ ...prev, productPrices: {}, bendingCharges: "" }));
      return;
    }

    const fetchOrderDetails = async () => {
      setLoading(true);
      try {
        const response = await axios.get(API_ENDPOINTS.CUSTOMER_ORDERS.GET(formData.id));
        const details = response.data;
        setSelectedOrderDetails(details);
        const initialPrices = details.products.reduce((acc, product) => {
          acc[product.id] = formData.productPrices[product.id] || "";
          return acc;
        }, {});
        setFormData((prev) => ({ ...prev, productPrices: initialPrices }));
      } catch (error) {
        setError("Failed to load order details.");
        setSelectedOrderDetails(null);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [formData.id]);

  // Auto-select enquiry from URL if present
  useEffect(() => {
    if (enquiryIdFromUrl && !formData.id) {
      setFormData((prev) => ({ ...prev, id: enquiryIdFromUrl }));
    }
    // eslint-disable-next-line
  }, [enquiryIdFromUrl]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePriceChange = (productId, value) => {
    setFormData((prev) => ({
      ...prev,
      productPrices: {
        ...prev.productPrices,
        [productId]: value ? parseFloat(value) : "",
      },
    }));
  };

  const handleChargesChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value ? parseFloat(value) : "",
    }));
  };

  const calculateProductAmount = (product) => {
    const price = parseFloat(formData.productPrices[product.id]) || 0;
  
    if (product.pieces != null) {
      // If 'pieces' is defined, just return the price
      return price;
    }
  
    // Else, use quantity (default to 0 if missing)
    const quantity = product.quantity ?? 0;
    return price * quantity;
  };
  

  const calculateOnlyProductCost = () => {
    if (!selectedOrderDetails?.products) return 0;
    return selectedOrderDetails.products.reduce(
      (sum, product) => sum + calculateProductAmount(product),
      0
    );
  };

  const calculateTotalAmount = () => {
    if (!selectedOrderDetails?.products) return 0;
    const productsTotal = selectedOrderDetails.products.reduce(
      (sum, product) => sum + calculateProductAmount(product),
      0
    );
    const bending =
      selectedOrderDetails?.customerOrder
        ? parseFloat(formData.bendingCharges) || 0
        : 0;
    const transportation = parseFloat(formData.transportationCharges) || 0;
    return productsTotal + bending + transportation;
  };

  const calculateFinalPrice = () => {
    const total = calculateTotalAmount();
    return total + total * 0.18;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!selectedOrderDetails?.customerOrder) {
      setError("No Enquiry selected.");
      return;
    }

    const invalidPrices = selectedOrderDetails.products.some(
      (product) => !formData.productPrices[product.id] || formData.productPrices[product.id] <= 0
    );
    if (invalidPrices) {
      setError("Please enter valid prices for all products.");
      return;
    }

    const products = selectedOrderDetails.products.map((product) => ({
      product_type_id: product.product_type_id,
      brand_id: product.brand_id,
      quantity: product.quantity,
      pieces: product.pieces,
      cost: parseFloat(formData.productPrices[product.id]) || 0,
    }));

    const payload = {
      custom_order_id: formData.id,
      app_user_id: parseInt(formData.app_user_id),
      bending_charges: parseFloat(formData.bendingCharges) || 0,
      transport_charges: parseFloat(formData.transportationCharges) || 0,
      total_amount: calculateFinalPrice(),
      gst_amount: calculateTotalAmount() * 0.18,
      status: "pending",
      products,
    };

    try {
      setLoading(true);
      await axios.post(API_ENDPOINTS.DEALER_QUOTATIONS.CREATE, payload);
      setSuccess("Quotation submitted successfully!");
      if (onSubmit) onSubmit(payload);
      setTimeout(() => navigate("/dealer-quotations"), 1500);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to submit quotation.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2, mb: 3 }}>
      <Card
        sx={{
          boxShadow: 3,
          borderRadius: "12px",
          p: { xs: 2, sm: 4 },
          margin: "auto",
          mt: 5,
        }}
      >
        {/* Title and Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          {/* <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
            Create Quotation
          </Typography> */}
        </Box>

        <Divider sx={{ my: 3 }} />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && (
          <Snackbar 
            open={!!success} 
            autoHideDuration={6000} 
            onClose={() => setSuccess(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>
          </Snackbar>
        )}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
            <CircularProgress />
          </Box>
        )}

        <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="dealer-name-label">Select Dealer</InputLabel>
              <Select
                labelId="dealer-name-label"
                name="app_user_id"
                value={formData.app_user_id}
                label="Select Dealer"
                onChange={handleChange}
                disabled={loading}
                required
              >
                <MenuItem value=""><em>Select Dealer</em></MenuItem>
                {dealers.map((dealer) => (
                  <MenuItem key={dealer.id} value={dealer.id}>
                    {dealer.company_name || `Dealer ${dealer.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="customer-order-label">Enquiry</InputLabel>
              <Select
                labelId="customer-order-label"
                name="id"
                value={formData.id}
                label="Enquiry"
                onChange={handleChange}
                disabled={loading}
                required
              >
                <MenuItem value=""><em>Select an Enquiry</em></MenuItem>
                {customerOrders.map((order) => (
                  <MenuItem key={order.id} value={order.id}>
                    {order.company_name || `Order ${order.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {selectedOrderDetails?.products?.length > 0 && (
          <Box mt={4}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
              Products (Enquiry ID: {formData.id})
            </Typography>
            <TableContainer component={Paper} sx={{ boxShadow: 1, borderRadius: "8px" }}>
              <Table sx={{ minWidth: 650 }} aria-label="product prices table">
                <TableHead sx={{ bgcolor: "#f0f0f0" }}>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Qty (Tons)</TableCell>
                    <TableCell align="right">Pieces</TableCell>
                    <TableCell align="right">Cost (Editable)</TableCell>
                    <TableCell>Brand</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedOrderDetails.products.map((product, idx) => (
                    <TableRow
                      key={product.id}
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                        '&:hover': { backgroundColor: '#f0f0f0' },
                      }}
                    >
                      <TableCell>{product.product_type?.name || 'N/A'}</TableCell>
                      <TableCell align="right">{product.quantity !== null ? product.quantity : 0}</TableCell>
                      <TableCell align="right">{product.pieces !== null ? product.pieces : 0}</TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          value={formData.productPrices[product.id] || ""}
                          onChange={(e) => handlePriceChange(product.id, e.target.value)}
                          inputProps={{ min: 0, step: "0.01" }}
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                      <TableCell>{product.brand?.name || 'N/A'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box mt={3}>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                Quotation Details
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                 
                    <TextField
                      fullWidth
                      label="Bending Charges"
                      name="bendingCharges"
                      type="number"
                      value={formData.bendingCharges}
                      onChange={handleChargesChange}
                      inputProps={{ min: 0, step: "0.01" }}
                    />
                  
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Transport Charges"
                    name="transportationCharges"
                    type="number"
                    value={formData.transportationCharges}
                    onChange={handleChargesChange}
                    inputProps={{ min: 0, step: "0.01" }}
                  />
                </Grid>
              </Grid>

              <Box mt={3}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Payment Terms:</Typography>
                  <Typography variant="body1">
                    {selectedOrderDetails?.customerOrder?.payment_terms === 'Credit' 
                      ? `${selectedOrderDetails.customerOrder.payment_terms} (${selectedOrderDetails.customerOrder.payment_terms_description})` 
                      : selectedOrderDetails?.customerOrder?.payment_terms || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Delivery Terms:</Typography>
                  <Typography variant="body1">
                    {selectedOrderDetails.customerOrder?.delivery_terms === 'Delivered To' 
                      ? `${selectedOrderDetails.customerOrder?.delivery_terms || 'N/A'}${selectedOrderDetails.customerOrder?.delivery_address ? ' - ' + selectedOrderDetails.customerOrder.delivery_address : ''}` 
                      : selectedOrderDetails.customerOrder?.delivery_terms || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Delivery Condition:</Typography>
                  <Typography variant="body1">{selectedOrderDetails?.customerOrder?.delivery_conditions || 'N/A'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Delivery Date:</Typography>
                  <Typography variant="body1">
                    {selectedOrderDetails?.customerOrder?.delivery_date === 'Immediate' ? (
                      selectedOrderDetails?.customerOrder?.delivery_date && selectedOrderDetails?.customerOrder?.delivery_date_hours ? 
                        `${selectedOrderDetails.customerOrder.delivery_date} Delivery ${selectedOrderDetails.customerOrder.delivery_date_hours} hours` : ''
                    ) : (
                      selectedOrderDetails?.customerOrder?.delivery_date && selectedOrderDetails?.customerOrder?.delivery_date_days ? 
                        `${selectedOrderDetails.customerOrder.delivery_date} Delivery in ${selectedOrderDetails.customerOrder.delivery_date_days} days` : ''
                    )}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Order By:</Typography>
                  <Typography variant="body1">
                    {selectedOrderDetails.customerOrder?.app_user?.contact_person || selectedOrderDetails.customerOrder?.app_user?.company_name || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box mt={3}>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                Summary of Charges
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Total Product Cost:</Typography>
                <Typography variant="body1">₹ {calculateOnlyProductCost().toFixed(2)}</Typography>
              </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Bending Charges:</Typography>
                  <Typography variant="body1">₹ {(parseFloat(formData.bendingCharges) || 0).toFixed(2)}</Typography>
                </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Transport Charges:</Typography>
                <Typography variant="body1">₹ {(parseFloat(formData.transportationCharges) || 0).toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>GST Amount (18%):</Typography>
                <Typography variant="body1">₹ {(calculateTotalAmount() * 0.18).toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Total Amount:</Typography>
                <Typography variant="body1">₹ {calculateFinalPrice().toFixed(2)}</Typography>
              </Box>
            </Box>
          </Box>
        )}

        <Box mt={4} display="flex" gap={2} justifyContent="center">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || !formData.id}
            size="large"
          >
            {loading ? <CircularProgress size={24} /> : "Submit Quotation"}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate("/dealer-quotations")}
            disabled={loading}
            size="large"
          >
            Cancel
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

export default DealerQuotationForm;